import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { api } from '../App';
import { Plus, X, Send } from 'lucide-react';

const RESEARCH_METHODS = [
  "Focus Groups", "IDIs", "Ethnography", "Online Communities", "Survey Design",
  "Conjoint", "MaxDiff", "Segmentation", "Report Writing", "Synthesis",
  "Workshop Facilitation", "Translation", "Back-translation", "Cultural Adaptation",
  "Diary Studies", "UX Research", "Card Sorting", "Eye-tracking", "Social Listening",
  "Text Analytics", "Quantitative Analysis", "Qualitative Analysis"
];

const SECTORS = [
  "FMCG", "Healthcare", "Beauty", "Financial Services", "Telecoms",
  "Retail", "Automotive", "Luxury", "B2B", "Tech", "Education",
  "Travel & Hospitality", "Media & Entertainment", "Real Estate", "Agriculture"
];

const OPPORTUNITY_TYPES = [
  { value: "gig", label: "Gig", description: "Short-term project or task" },
  { value: "job_role", label: "Job Role", description: "Full-time or part-time position" },
  { value: "contract", label: "Contract", description: "Fixed-term engagement" },
  { value: "rfp", label: "RFP", description: "Request for Proposal" }
];

export default function CreateOpportunity() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    opportunity_type: 'gig',
    title: '',
    company_name: '',
    description: '',
    deliverables: '',
    method: '',
    sector: '',
    skills_needed: [],
    languages_required: [],
    budget_min: '',
    budget_max: '',
    timeline: '',
    duration: '',
    location_type: 'remote',
    location: ''
  });
  const [newSkill, setNewSkill] = useState('');
  const [newLanguage, setNewLanguage] = useState('');
  
  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };
  
  const addItem = (field, value, setter) => {
    if (value.trim()) {
      setForm(prev => ({
        ...prev,
        [field]: [...prev[field], value.trim()]
      }));
      setter('');
    }
  };
  
  const removeItem = (field, index) => {
    setForm(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.title.trim() || !form.description.trim()) {
      alert('Please fill in the title and description');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...form,
        budget_min: form.budget_min ? parseFloat(form.budget_min) : null,
        budget_max: form.budget_max ? parseFloat(form.budget_max) : null
      };
      
      await api.post('/opportunities', payload);
      navigate('/client/opportunities');
    } catch (error) {
      console.error('Error creating opportunity:', error);
      alert(error.response?.data?.detail || 'Failed to create opportunity');
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <DashboardLayout title="Post New Opportunity">
      <form onSubmit={handleSubmit} className="max-w-4xl">
        {/* Opportunity Type */}
        <div className="bg-white border border-neutral-200 p-6 mb-6">
          <h3 className="font-semibold text-lg mb-4" style={{ fontFamily: 'Outfit' }}>
            Opportunity Type
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {OPPORTUNITY_TYPES.map((type) => (
              <button
                key={type.value}
                type="button"
                onClick={() => handleChange('opportunity_type', type.value)}
                className={`p-4 border text-left transition-colors ${
                  form.opportunity_type === type.value
                    ? 'border-[#0D7377] bg-[#E6F3F4]'
                    : 'border-neutral-200 hover:border-neutral-300'
                }`}
              >
                <span className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 inline-block mb-2 ${
                  form.opportunity_type === type.value
                    ? 'bg-[#0D7377] text-white'
                    : 'bg-neutral-100 text-neutral-600'
                }`}>
                  {type.label}
                </span>
                <p className="text-sm text-neutral-600">{type.description}</p>
              </button>
            ))}
          </div>
        </div>
        
        {/* Basic Info */}
        <div className="bg-white border border-neutral-200 p-6 mb-6">
          <h3 className="font-semibold text-lg mb-4" style={{ fontFamily: 'Outfit' }}>
            Basic Information
          </h3>
          
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  data-testid="title-input"
                  placeholder="e.g., Qual Moderator - 6 Focus Groups"
                  value={form.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-200 focus:border-[#0D7377] outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Company / Brand Name
                </label>
                <input
                  type="text"
                  data-testid="company-input"
                  placeholder="e.g., Western India Spices brand"
                  value={form.company_name}
                  onChange={(e) => handleChange('company_name', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-200 focus:border-[#0D7377] outline-none"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Description *
              </label>
              <textarea
                data-testid="description-input"
                placeholder="Describe the research opportunity, objectives, and what you're looking for..."
                value={form.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={5}
                className="w-full px-3 py-2 border border-neutral-200 focus:border-[#0D7377] outline-none resize-none"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Deliverables
              </label>
              <textarea
                data-testid="deliverables-input"
                placeholder="What are the expected deliverables? e.g., Managing internal and external stakeholders. Handle voice of channel."
                value={form.deliverables}
                onChange={(e) => handleChange('deliverables', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-neutral-200 focus:border-[#0D7377] outline-none resize-none"
              />
            </div>
          </div>
        </div>
        
        {/* Method & Sector */}
        <div className="bg-white border border-neutral-200 p-6 mb-6">
          <h3 className="font-semibold text-lg mb-4" style={{ fontFamily: 'Outfit' }}>
            Category
          </h3>
          
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Primary Method
              </label>
              <select
                data-testid="method-select"
                value={form.method}
                onChange={(e) => handleChange('method', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-200 focus:border-[#0D7377] outline-none bg-white"
              >
                <option value="">Select a method</option>
                {RESEARCH_METHODS.map(method => (
                  <option key={method} value={method}>{method}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Sector
              </label>
              <select
                data-testid="sector-select"
                value={form.sector}
                onChange={(e) => handleChange('sector', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-200 focus:border-[#0D7377] outline-none bg-white"
              >
                <option value="">Select a sector</option>
                {SECTORS.map(sector => (
                  <option key={sector} value={sector}>{sector}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        
        {/* Skills & Languages */}
        <div className="bg-white border border-neutral-200 p-6 mb-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit' }}>Skills Needed</h3>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  data-testid="skill-input"
                  placeholder="Add a skill..."
                  value={newSkill}
                  onChange={(e) => setNewSkill(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addItem('skills_needed', newSkill, setNewSkill))}
                  className="flex-1 px-3 py-2 border border-neutral-200 focus:border-[#0D7377] outline-none"
                />
                <button
                  type="button"
                  onClick={() => addItem('skills_needed', newSkill, setNewSkill)}
                  className="btn-client px-3 py-2"
                >
                  <Plus size={18} />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {form.skills_needed.map((skill, i) => (
                  <span key={i} className="px-3 py-1.5 bg-[#E6F3F4] text-[#0D7377] text-sm font-medium flex items-center gap-2">
                    {skill}
                    <button type="button" onClick={() => removeItem('skills_needed', i)} className="hover:text-red-500">
                      <X size={14} />
                    </button>
                  </span>
                ))}
              </div>
            </div>
            
            <div>
              <h3 className="font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit' }}>Languages Required</h3>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  data-testid="language-input"
                  placeholder="e.g., English, Hindi"
                  value={newLanguage}
                  onChange={(e) => setNewLanguage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addItem('languages_required', newLanguage, setNewLanguage))}
                  className="flex-1 px-3 py-2 border border-neutral-200 focus:border-[#0D7377] outline-none"
                />
                <button
                  type="button"
                  onClick={() => addItem('languages_required', newLanguage, setNewLanguage)}
                  className="btn-client px-3 py-2"
                >
                  <Plus size={18} />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {form.languages_required.map((lang, i) => (
                  <span key={i} className="px-3 py-1 bg-neutral-100 text-neutral-700 text-sm flex items-center gap-2">
                    {lang}
                    <button type="button" onClick={() => removeItem('languages_required', i)} className="hover:text-red-500">
                      <X size={14} />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Budget & Timeline */}
        <div className="bg-white border border-neutral-200 p-6 mb-6">
          <h3 className="font-semibold text-lg mb-4" style={{ fontFamily: 'Outfit' }}>
            Budget & Timeline
          </h3>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Budget Min (₹)
              </label>
              <input
                type="number"
                data-testid="budget-min-input"
                placeholder="e.g., 100000"
                value={form.budget_min}
                onChange={(e) => handleChange('budget_min', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-200 focus:border-[#0D7377] outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Budget Max (₹)
              </label>
              <input
                type="number"
                data-testid="budget-max-input"
                placeholder="e.g., 500000"
                value={form.budget_max}
                onChange={(e) => handleChange('budget_max', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-200 focus:border-[#0D7377] outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Timeline
              </label>
              <input
                type="text"
                data-testid="timeline-input"
                placeholder="e.g., June 2026"
                value={form.timeline}
                onChange={(e) => handleChange('timeline', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-200 focus:border-[#0D7377] outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Duration
              </label>
              <input
                type="text"
                data-testid="duration-input"
                placeholder="e.g., 4-6 weeks"
                value={form.duration}
                onChange={(e) => handleChange('duration', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-200 focus:border-[#0D7377] outline-none"
              />
            </div>
          </div>
        </div>
        
        {/* Location */}
        <div className="bg-white border border-neutral-200 p-6 mb-6">
          <h3 className="font-semibold text-lg mb-4" style={{ fontFamily: 'Outfit' }}>
            Location
          </h3>
          
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Location Type
              </label>
              <select
                data-testid="location-type-select"
                value={form.location_type}
                onChange={(e) => handleChange('location_type', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-200 focus:border-[#0D7377] outline-none bg-white"
              >
                <option value="remote">Remote</option>
                <option value="onsite">On-site</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Location (if applicable)
              </label>
              <input
                type="text"
                data-testid="location-input"
                placeholder="e.g., Mumbai, Pune"
                value={form.location}
                onChange={(e) => handleChange('location', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-200 focus:border-[#0D7377] outline-none"
              />
            </div>
          </div>
        </div>
        
        {/* Submit */}
        <div className="flex gap-4">
          <button
            type="submit"
            data-testid="submit-opportunity"
            disabled={saving}
            className="btn-client px-8 py-3 flex items-center gap-2"
          >
            {saving ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <>
                <Send size={18} />
                Post Opportunity
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => navigate('/client/opportunities')}
            className="px-8 py-3 border border-neutral-200 hover:bg-neutral-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </DashboardLayout>
  );
}
